import * as Tooltip from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { type AnimationProps, motion, useReducedMotion } from "framer-motion";
import React from "react";
import { FormattedMessage } from "react-intl";
import { change } from "redux-form";

import { COURSES_TIPS } from "~/common/constants";
import { CourseId, getCourseTag, isOneArchIncludedInCourse, isPowerCapCourse, isPromoCodeAvailableForCourse, isRetainersCourse, versionize } from "~/common/courses";
import { Arch } from "~/common/prescription";
import { RedesignQuestionTooltip } from "~/common/redesign-v1/components/question-tooltip";
import { LabelledRadio, LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { TooltipRootClickable } from "~/components/ui/tooltip";
import { Wrapper } from "~/components/ui/wrapper";
import { getCourseVersion, isCBCTIncludedInCourse, type TCourseInstallment } from "~/slices/course-installment";
import { addInstructions, removeInstructions, selectInstructions } from "~/slices/instructions";
import { selectCorrectionFormValues } from "~/slices/redux-form";
import { useAppDispatch, useAppSelector } from "~/store";
import type { TPatient } from "~/types/patient";
import type { SetDirtyFn } from "~/types/patient-new";

export function PatientNewCourse({
  setDirty,
  courseInstallment,
  additionalServices,
  hideCTWarning = false,
}: {
  setDirty: SetDirtyFn;
  courseInstallment: TCourseInstallment;
  additionalServices?: React.ReactNode;
  hideCTWarning?: boolean;
}) {
  const instructions = useAppSelector(selectInstructions);
  const formValues = useAppSelector(selectCorrectionFormValues);
  const dispatch = useAppDispatch();

  function selectCourse(course_id: CourseId) {
    dispatch(addInstructions({ course_id }));
    setDirty("course_id", course_id);

    const coursePaymentOptions = courseInstallment[course_id];

    if (instructions.course_id != course_id && instructions.payment_option_id && !(instructions.payment_option_id in coursePaymentOptions)) {
      dispatch(addInstructions({ payment_option_id: null }));
    }

    if (formValues?.arch == Arch.BOTH && isOneArchIncludedInCourse(course_id)) {
      dispatch(change("correction", "arch", null));
    }

    dispatch(addInstructions({ deep_cbct: null }));

    if (isRetainersCourse(course_id) || isPowerCapCourse(course_id)) {
      dispatch(removeInstructions({ sber_credit: false }));
    }

    if (!isPromoCodeAvailableForCourse(course_id)) {
      dispatch(removeInstructions({ promo_code: undefined }));
    }
  }

  const { course_id = null, deep_cbct } = instructions;

  // The redesign page's top-level `gap-6` supplies the 24px spacing; an additional margin would double it.
  // a leftover `marginTop: "20px"` here was stacking on top of that gap for ~44px total. Already inert on
  // the legacy page (its own `!important` wildcard divider rule overrides any margin here regardless).
  return (
    <div>
      <PatientNewCourseOptions selectedCourse={course_id} courseInstallment={courseInstallment} onCourseChange={selectCourse} additionalServices={additionalServices} />

      {/*
       * If you use AnimatePresence here, then if you switch from course that displays this warning to
       * retainers, then material options have a nasty buggy layout shift on the right of the screen.
       * So i decided to remove AnimatePresence alltogether, feels better for that edge case.
       */}
      {hideCTWarning || !shouldShowScanUploadCTWarning(courseInstallment, course_id, deep_cbct) ? null : <ScanUploadCTWarning />}
    </div>
  );
}

function PatientNewCourseOptions(props: { selectedCourse: number | null; courseInstallment: TCourseInstallment; onCourseChange(course: CourseId): void; additionalServices?: React.ReactNode }) {
  const { selectedCourse, courseInstallment, onCourseChange } = props;

  const baseOrder: CourseId[] = [
    CourseId.EXPERT,
    CourseId.TRANSPARENT,
    CourseId.LIMITLESS,
    CourseId.FULL,
    CourseId.TEEN,
    CourseId.SHORT,
    CourseId.SINGLE_ARCH,
    CourseId.SUPER_SHORT,
    CourseId.CHILDREN,
    CourseId.CHILDREN_SHORT,
    CourseId.PLAN_3D,
    CourseId.RETAINERS,
    CourseId.POWER_CAP,
    CourseId.ADDITIONAL_ALIGNERS,
  ];

  const sortedCourseIds = [...baseOrder].sort((a, b) => {
    if (a === CourseId.EXPERT) return -1;
    if (b === CourseId.EXPERT) return 1;

    const aExclusive = courseInstallment?.[a]?.exclusive === true;
    const bExclusive = courseInstallment?.[b]?.exclusive === true;

    if (aExclusive === bExclusive) {
      return 0;
    }

    return aExclusive ? -1 : 1;
  });

  return (
    <CourseRadioGroup>
      {sortedCourseIds.map((courseId) => (
        <React.Fragment key={courseId}>
          <CourseRadio
            selectedCourse={selectedCourse}
            courseInstallment={courseInstallment}
            onCourseChange={onCourseChange}
            course={courseId}
            isNew={courseId === CourseId.LIMITLESS}
            tooltip={
              courseId === CourseId.CHILDREN ? (
                <ChildrenV3Tooltip />
              ) : courseId === CourseId.CHILDREN_SHORT ? (
                <ChildrenShortTooltip />
              ) : courseId === CourseId.POWER_CAP ? (
                <PowerCapCourseTooltip />
              ) : undefined
            }
          />
          {props.selectedCourse === courseId && props.additionalServices ? <div className="tw-ml-12 [&>.form-group]:tw-mb-0">{props.additionalServices}</div> : null}
        </React.Fragment>
      ))}
    </CourseRadioGroup>
  );
}

export function CourseRadioGroup({ children }: React.PropsWithChildren<object>) {
  return (
    <LabelledRadioGroup
      className="form-group"
      label={
        <>
          <FormattedMessage id="patient.form.course.title" />
          <RedesignQuestionTooltip side="bottom">
            <FormattedMessage id="course.type.tooltip" />
            <a href="/api/v1/manuals/document/2" rel="noopener noreferrer" target="_blank">
              <FormattedMessage id="tooltip.chart" />
            </a>
            <br />
            <FormattedMessage id="course.type.tooltip.sec" />
          </RedesignQuestionTooltip>
        </>
      }
      labelProps={{
        id: "validation-course_id",
        size: "xl",
      }}
      required
    >
      {children}
    </LabelledRadioGroup>
  );
}

export function CourseRadio({
  course,
  selectedCourse,
  courseInstallment,
  onCourseChange,
  patient,
  isNew = false,
  tooltip,
  image,
}: {
  course: CourseId;
  selectedCourse: number | null;
  courseInstallment: TCourseInstallment;
  onCourseChange(newCourse: CourseId): void;
  patient?: TPatient;
  isNew?: boolean;
  tooltip?: React.ReactChild | null;
  image?: React.ReactChild;
}) {
  const courseVersion = getCourseVersion(courseInstallment, course);

  // `getCourseVersion` pins retainers / power cap to V1 even when the installment map omits them
  // (USA builds skip POWER_CAP). Hide the radio unless the API actually offered the course.
  if (courseVersion == null || (isPowerCapCourse(course) && courseInstallment[course] == null)) {
    return null;
  }

  const title = versionize(getCourseTag(course), courseVersion);
  const description = versionize(`${getCourseTag(course)}_COMMENT`, courseVersion);

  const tips = COURSES_TIPS[course] ?? [];

  const isExclusive = courseInstallment?.[course]?.exclusive === true;

  return (
    <LabelledRadio
      label={
        <Wrapper when={tips.length > 0} render={(children) => <TipsTooltip tips={tips}>{children}</TipsTooltip>}>
          <div className="course_title">
            {image}
            {image ? <br /> : null}

            <span className={course == selectedCourse ? "boldText" : undefined}>
              {isNew ? (
                <span className="rd-course-badge-new tw-mr-2 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-[#f97316] tw-px-2 tw-py-0.5 tw-align-middle tw-text-xs tw-font-bold tw-leading-4 tw-text-white">
                  NEW
                </span>
              ) : null}
              {isExclusive ? (
                <span className="course_badge_exclusive tw-mr-2 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-2 tw-py-0.5 tw-align-middle tw-text-xs tw-font-bold tw-leading-4 tw-text-primary-foreground">
                  EXCLUSIVE
                </span>
              ) : null}{" "}
              {/* @ts-expect-error some courses don't have all the versions of the other courses */}
              <FormattedMessage id={title} defaultMessage=" " />
            </span>

            {/* @ts-expect-error some courses don't have all the versions of the other courses */}
            <FormattedMessage id={description} defaultMessage=" " />
            {tooltip}
          </div>
        </Wrapper>
      }
      align="start"
      name="course_id"
      defaultValue={course}
      defaultChecked={patient?.course.course_id == course}
      onChange={() => onCourseChange(course)}
    />
  );
}

function TipsTooltip({ tips, children }: React.PropsWithChildren<{ tips: string[] }>) {
  return (
    <Tooltip.Provider delayDuration={350}>
      <TooltipRootClickable>
        <Tooltip.Trigger className="tw-cursor-default tw-border-none tw-bg-transparent tw-px-0 tw-text-left" onClick={(event) => event.currentTarget.closest("label")?.click()}>
          {children}
        </Tooltip.Trigger>

        <Tooltip.Portal>
          {/* `data-redesign-v1` applies the shared redesign tooltip tokens to this direct Radix usage.
              `TooltipContent` (~/common/redesign-v1/ui/tooltip.tsx) — thick default-grey 3px border,
              off-white `#f7f7f7` fill, plain `rounded` (4px), no relation to the redesign's card/border
              tokens. Radix PORTALS this content out of the RedesignRoot subtree, so `--rd-*` tokens don't
              resolve without re-scoping via `data-redesign-v1` on the portaled element itself — the same
              requirement `TooltipContent`/`SelectContent` already document and rely on. */}
          <Tooltip.Content
            data-redesign-v1
            side="bottom"
            align="start"
            sideOffset={5}
            className={clsx(
              "tw-max-w-[100vw] tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-card tw-px-4 tw-py-3 tw-text-card-foreground tw-shadow-md sm:tw-max-w-2xl",
              "tw-max-h-[min(70vh,480px)] tw-overflow-auto",
              "motion-safe:tw-duration-300 motion-safe:tw-animate-in",
              "data-[state=delayed-open]:tw-fade-in-0",
              "data-[state=delayed-open]:tw-slide-in-from-top-1",
            )}
          >
            <ul className="tw-m-0 tw-space-y-0.5 tw-pl-3.5 tw-text-sm">
              {tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </Tooltip.Content>
        </Tooltip.Portal>
      </TooltipRootClickable>
    </Tooltip.Provider>
  );
}

export function shouldShowScanUploadCTWarning(courseInstallment: TCourseInstallment, course_id: CourseId | number | null, deep_cbct: boolean | null | undefined): boolean {
  return (course_id != null && isCBCTIncludedInCourse(courseInstallment, course_id)) || deep_cbct == true;
}

export function ScanUploadCTWarning() {
  const shouldReduceMotion = useReducedMotion();

  const animationProps: AnimationProps = {
    initial: { height: 0 },
    animate: { height: "auto", transition: { duration: 0.5 } },
    exit: { height: 0 },
  };

  return (
    <div className="scan-upload-ct-warning col-xs-12 tw-overflow-hidden">
      <motion.p className="tw-m-0" {...(shouldReduceMotion ? {} : animationProps)}>
        <span className="alert-warning tw-block tw-p-4">
          <FormattedMessage id="SCAN_UPLOAD_CT_WARNING" />
        </span>
        <span className="scan-upload-ct-warning-spacer tw-block tw-h-5" />
      </motion.p>
    </div>
  );
}

export function PowerCapCourseTooltip() {
  return (
    <RedesignQuestionTooltip
      testId="power-cap-course-tooltip"
      // Same viewport clamp as the shared default (question-tooltip.tsx) — this tooltip's own 420px
      // override is wider still, so without it the overflow off a mobile screen's right edge was worse.
      contentClassName="tw-max-w-[min(420px,calc(100vw-2rem))] tw-overflow-y-auto tw-text-left"
    >
      <p className="tw-m-0 tw-font-semibold">Силовая капа</p>
      <p className="tw-m-0 tw-mt-2">
        Силовая капа применяется на финальных этапах лечения для устранения небольших отклонений от запланированного результата. Это альтернатива малой коррекции, позволяющая объединить два шага
        перемещения в одну каппу.
      </p>
      <p className="tw-m-0 tw-mt-2 tw-font-semibold">Возможности и рекомендации:</p>
      <ul className="tw-m-0 tw-mt-2 tw-list-none tw-space-y-0.5 tw-p-0">
        <li>
          · <strong>Зона перемещения:</strong> от клыка до клыка. Перемещение моляров и премоляров в рамках силовой капы не предусмотрено;
        </li>
        <li>
          · <strong>Ротация, ангуляция или торк:</strong> не более 5 градусов;
        </li>
        <li>
          · <strong>Линейное перемещение</strong> (вестибулярно/нёбно, дистально/мезиально, интрузия): не более 0,5 мм;
        </li>
        <li>
          · <strong>Экструзия:</strong> не рекомендуется;
        </li>
        <li>
          · <strong>Аттачменты:</strong> перемещения выполняются без аттачментов.
        </li>
      </ul>
      <p className="tw-m-0 tw-mt-2 tw-italic">Пожалуйста, убедитесь, что ваш запрос соответствует этим критериям, чтобы избежать возврата заказа на пересмотр курса лечения.</p>
      <p className="tw-m-0 tw-mt-2">Цена появится, когда вы выберете челюсть.</p>
    </RedesignQuestionTooltip>
  );
}

export function ChildrenV3Tooltip() {
  return (
    <RedesignQuestionTooltip>
      Доступен к заказу при соблюдении у пациента следующих условий:
      <ol className="tw-mt-2 tw-list-none tw-space-y-0.5 tw-pl-2 tw-italic">
        <li>А. Должны быть прорезавшимися постоянные первые моляры</li>
        <li>Б. Не менее 2 постоянных резцов должны быть прорезавшимися не менее, чем на 2/3</li>
        <li>В. Не менее 2 зубов (III или IV или V) в одном квадранте должны быть в наличии</li>
        <li>Г. Отсутствие постоянных 7 зубов</li>
      </ol>
      <p className="tw-m-0">
        Курс включает в себя неограниченное количество элайнеров и коррекций <strong>в течение 18 месяцев</strong>.
      </p>
      <p className="tw-m-0 tw-mt-2">
        <strong>Внимание!</strong> Дубликаты не входят в стоимость курса и при необходимости заказываются отдельно в соответствии с действующим прайсом.
      </p>
    </RedesignQuestionTooltip>
  );
}

export function ChildrenShortTooltip() {
  return (
    <RedesignQuestionTooltip>
      Первый комплект доступен для заказа при соблюдении у пациента следующих условий:
      <ol className="tw-mt-2 tw-list-none tw-space-y-0.5 tw-pl-2 tw-italic">
        <li>А. Должны быть прорезавшимися постоянные первые моляры</li>
        <li>Б. Не менее 2 постоянных резцов должны быть прорезавшимися не менее, чем на 2/3</li>
        <li>В. Не менее 2 зубов (III или IV или V) в одном квадранте должны быть в наличии</li>
        <li>Г. Отсутствие постоянных 7 зубов</li>
      </ol>
      <p className="tw-m-0">
        Комплект включает в себя до <strong>24 КАП</strong>, которые возможно использовать для лечения как на одной, так и на двух челюстях.
      </p>
    </RedesignQuestionTooltip>
  );
}
